import { AsyncMultiSelectField, CharField, LinkField, SingleSelectField, SwitchField } from "@inmagik/magik-ui"
import { FieldArray, getIn, useFormikContext } from "formik"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { Col, Row } from "react-bootstrap"
import { WysiwygField } from "../../components/WysiwygField"
import { RESOURCE_TYPOLOGIES } from "../../constants"
import { useCreateKeyword, useKeywordLazy, useKeywordsLazy } from "../../hooks/keywords"
import { useCreateNamedEntity, useNamedEntitiesLazy, useNamedEntityLazy } from "../../hooks/named-entities"
import type { NamedEntity, Resource } from "../../types"
import { transformErrorForForm } from "../../utils/utils"
import KeywordModal from "../Keywords/KeywordModal"
import NamedEntityModal from "../NamedEntities/NamedEntityModal"

export default function ResourceForm({ col = 8 }: { col?: number }) {
  const { setFieldValue, values, errors, submitCount } = useFormikContext<Resource>()

  const fetchNamedEntitiesEditors = useNamedEntitiesLazy({ entity_type: "EDITOR" })
  const fetchNamedEntitiesPersons = useNamedEntitiesLazy({ entity_type: ["AUTHOR", "PHOTOGRAPHER", "DESIGNER"] })
  const fetchKeywords = useKeywordsLazy()
  const fetchKeyword = useKeywordLazy()
  const fetchNamedEntity = useNamedEntityLazy()
  const createNamedEntity = useCreateNamedEntity()
  const createKeyword = useCreateKeyword()

  const [modalStateCreateNamedEntity, modalActionsCreateNamedEntity] = useModalTrigger()
  const [modalStateCreateKeyword, modalActionsCreateKeyword] = useModalTrigger()

  return (
    <Col xs={col} className="d-flex flex-column align-items-stretch gap-4">
      <CharField
        label="Titolo"
        layout="horizontal"
        value={values.title}
        onChange={(value) => {
          setFieldValue("title", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "title") : undefined}
        errorMessage={getIn(errors, "title")}
        required
      />

      <SingleSelectField
        label="Tipologia"
        layout="horizontal"
        required
        placeholder="Seleziona una tipologia"
        options={RESOURCE_TYPOLOGIES}
        value={RESOURCE_TYPOLOGIES.find((opt) => opt.value === values.typology) || null}
        onChange={(option) => {
          setFieldValue("typology", option ? option.value : null)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "typology") : undefined}
        errorMessage={getIn(errors, "typology")}
      />

      <WysiwygField
        label="Note"
        layout="horizontal"
        value={values.note || ""}
        onChange={(value) => {
          setFieldValue("note", value)
        }}
      />

      <LinkField
        label="Url embedding"
        layout="horizontal"
        value={values.embed_url || ""}
        onChange={(value) => {
          setFieldValue("embed_url", value)
        }}
      />

      <FieldArray name="link_urls">
        {({ push, remove }) => (
          <>
            {(values.link_urls || []).map((link, index) => (
              <>
                <LinkField
                  layout="horizontal"
                  label="Link fonte"
                  inputColSize={8}
                  value={link}
                  onChange={(value) => {
                    setFieldValue(`link_urls.${index}`, value)
                  }}
                  isValid={submitCount > 0 ? !getIn(errors, `link_urls.${index}`) : undefined}
                  errorMessage={getIn(errors, `link_urls.${index}`)}
                />
                <Row>
                  <Col md={4} />
                  <Col md={8}>
                    <button
                      type="button"
                      style={{ width: "fit-content" }}
                      className="btn btn-danger"
                      onClick={() => {
                        remove(index)
                      }}
                    >
                      Rimuovi
                    </button>
                  </Col>
                </Row>
              </>
            ))}
            <Row>
              <Col md={4} />
              <Col md={8}>
                <button
                  type="button"
                  className="btn btn-primary mt-2"
                  onClick={() => {
                    push("")
                  }}
                >
                  Aggiungi link di fonte
                </button>
              </Col>
            </Row>
          </>
        )}
      </FieldArray>
      <AsyncMultiSelectField
        label="Case editrici"
        layout="horizontal"
        placeholder="Seleziona uno o più case editrici"
        onChange={(value) => {
          setFieldValue("editors", value)
        }}
        clearable
        value={values.editors || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((editor) => ({ label: editor.name, value: editor.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesEditors({ search: inputValue }).then((editors) => {
            return editors.results?.map((editor) => ({
              label: editor.name,
              value: editor.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("EDITOR")
          }}
        />
      </AsyncMultiSelectField>
      <AsyncMultiSelectField
        label="Persone"
        layout="horizontal"
        placeholder="Seleziona uno o più persone"
        onChange={(value) => {
          setFieldValue("persons", value)
        }}
        clearable
        value={values.persons || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((author) => ({ label: author.name, value: author.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesPersons({ search: inputValue }).then((authors) => {
            return authors.results?.map((author) => ({
              label: author.name,
              value: author.id,
            }))
          })
        }}
      />

      <AsyncMultiSelectField
        label="Temi"
        layout="horizontal"
        placeholder="Seleziona uno o più temi"
        onChange={(value) => {
          setFieldValue("keywords", value)
        }}
        clearable
        value={values.keywords || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchKeyword(id).then((keyword) => ({ label: keyword.name, value: keyword.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchKeywords({ search: inputValue }).then((keywords) => {
            return keywords.results?.map((keyword) => ({
              label: keyword.name,
              value: keyword.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateKeyword.open()
          }}
        />
      </AsyncMultiSelectField>
      <SwitchField
        label="Bozza"
        layout="horizontal"
        value={values.draft}
        onChange={(checked) => {
          setFieldValue("draft", checked)
        }}
      />
      {modalStateCreateNamedEntity.isOpen && (
        <NamedEntityModal
          show={modalStateCreateNamedEntity.isOpen}
          type={modalStateCreateNamedEntity.value}
          onClose={modalActionsCreateNamedEntity.close}
          save={(valuesEntity, actions) => {
            return createNamedEntity
              .mutateAsync(valuesEntity)
              .then((namedEntity: NamedEntity) => {
                if (valuesEntity.entity_types.includes("EDITOR"))
                  setFieldValue("editors", [...((values.editors as number[]) || []), namedEntity.id])

                modalActionsCreateNamedEntity.close()
              })
              .catch((e: any) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title={
            modalStateCreateNamedEntity.value === "AUTHOR"
              ? "Crea nuovo scrittore"
              : modalStateCreateNamedEntity.value === "EDITOR"
                ? "Crea nuova casa editrice"
                : modalStateCreateNamedEntity.value === "PHOTOGRAPHER"
                  ? "Crea nuovo fotografo"
                  : modalStateCreateNamedEntity.value === "TYPOLOGY"
                    ? "Crea nuova tipologia"
                    : "Crea nuovo formato"
          }
        />
      )}
      {modalStateCreateKeyword.isOpen && (
        <KeywordModal
          show={modalStateCreateKeyword.isOpen}
          onClose={modalActionsCreateKeyword.close}
          save={(valuesKeyword, actions) => {
            return createKeyword
              .mutateAsync(valuesKeyword)
              .then((keyword) => {
                setFieldValue("keywords", [...((values.keywords as number[]) || []), keyword.id])
                modalActionsCreateKeyword.close()
              })
              .catch((e: any) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Crea nuovo tema"
        />
      )}
    </Col>
  )
}
